import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Bot, Send, X, Sparkles, Loader2, CloudRain, Sun, CloudSnow, CalendarClock,
  ArrowRight, Check, XCircle, Cloud,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const SESSION_ID = "planer-" + Math.random().toString(36).slice(2, 9);

const QUICK_PROMPTS = [
  "Welche Termine sind wetterkritisch?",
  "Zeige die Wetterprognose der nächsten 7 Tage",
  "Finde einen besseren Termin für den nächsten Regen-Einsatz",
];

function fmtDe(iso) {
  try {
    return new Date(iso).toLocaleString("de-DE", {
      weekday: "short", day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function WeatherIcon({ condition, className = "w-4 h-4" }) {
  const c = (condition || "").toLowerCase();
  if (c.includes("gewitter") || c.includes("regen") || c.includes("niesel"))
    return <CloudRain className={className} />;
  if (c.includes("schnee") || c.includes("griesel"))
    return <CloudSnow className={className} />;
  if (c.includes("nebel") || c.includes("bewölkt") || c.includes("wolke"))
    return <Cloud className={className} />;
  return <Sun className={className} />;
}

function categoryStyle(cat) {
  if (cat === "gut") return "bg-[#DCEBD4] text-[#3B5C2C] border-[#B0C89D]";
  if (cat === "akzeptabel") return "bg-[#FDF3D8] text-[#8A6100] border-[#F5D876]";
  if (cat === "eingeschränkt") return "bg-[#F5D9CC] text-[#7A2E14] border-[#EEB596]";
  return "bg-[#EFCAB8] text-[#7A2E14] border-[#DA9575]";
}

function SuggestionCard({ action, onAccept, onReject, busy }) {
  const [notify, setNotify] = useState(action.notify_customer !== false);
  const w = action.suggested_weather || {};
  const cw = action.appointment?.weather || {};

  return (
    <div className="mt-3 bg-white border border-[#C4CFB1] rounded-xl overflow-hidden" data-testid={`assistant-suggestion-${action.appointment_id}`}>
      <div className="px-4 py-3 bg-[#F5F6F0] border-b border-[#C4CFB1]">
        <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-[#6B7F3E]">
          <Sparkles className="w-3.5 h-3.5" />
          Terminverschiebung vorgeschlagen
        </div>
        <div className="mt-1 text-sm font-medium text-[#1A201A]">{action.appointment?.title}</div>
        {action.appointment?.customer_name && (
          <div className="text-[11px] text-[#5C6B5D]">Kunde: {action.appointment.customer_name}</div>
        )}
      </div>
      <div className="p-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="border border-[#E5E7EB] rounded-lg p-3 bg-[#FAFAF5]">
            <div className="text-[10px] font-medium uppercase tracking-wider text-[#5C6B5D] mb-1">Aktuell</div>
            <div className="text-xs font-medium text-[#1A201A] mb-2">{fmtDe(action.current_iso || action.appointment?.starts_at)}</div>
            {cw.available && (
              <div className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${categoryStyle(cw.category)}`}>
                <WeatherIcon condition={cw.condition} className="w-3 h-3" />
                {cw.category} · {cw.condition}
              </div>
            )}
          </div>
          <div className="border-2 border-[#6B7F3E] rounded-lg p-3 bg-[#F5F6F0]">
            <div className="text-[10px] font-medium uppercase tracking-wider text-[#6B7F3E] mb-1 flex items-center gap-1">
              Vorschlag <ArrowRight className="w-2.5 h-2.5" />
            </div>
            <div className="text-xs font-medium text-[#1A201A] mb-2">{fmtDe(action.suggested_iso)}</div>
            {w.available && (
              <div className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${categoryStyle(w.category)}`}>
                <WeatherIcon condition={w.condition} className="w-3 h-3" />
                {w.category} · {w.condition}
              </div>
            )}
          </div>
        </div>
        {action.reason && (
          <div className="mt-3 text-xs text-[#4A5D4E] italic">
            „{action.reason}"
          </div>
        )}
        <label className="mt-3 flex items-center gap-2 text-xs text-[#2C3E2D] cursor-pointer">
          <input
            type="checkbox"
            checked={notify}
            onChange={(e) => setNotify(e.target.checked)}
            data-testid="assistant-notify-checkbox"
            className="accent-[#6B7F3E]"
          />
          Kunde per E-Mail über Verschiebung informieren
        </label>
        <div className="mt-3 flex gap-2">
          <Button
            size="sm"
            className="bg-[#6B7F3E] hover:bg-[#5A6B34] text-white flex-1"
            onClick={() => onAccept(action, notify)}
            disabled={busy}
            data-testid={`assistant-accept-${action.appointment_id}`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Check className="w-3.5 h-3.5 mr-1" /> Annehmen & umbuchen</>}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onReject(action)}
            disabled={busy}
            data-testid={`assistant-reject-${action.appointment_id}`}
          >
            <XCircle className="w-3.5 h-3.5 mr-1" /> Ablehnen
          </Button>
        </div>
      </div>
    </div>
  );
}

function Message({ msg, onAccept, onReject, busyActionId }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] ${isUser ? "" : "w-full"}`}>
        {!isUser && (
          <div className="flex items-center gap-1.5 mb-1 text-[10px] font-medium text-[#6B7F3E]">
            <Sparkles className="w-3 h-3" /> Planer
          </div>
        )}
        <div
          className={`rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
            isUser
              ? "bg-[#6B7F3E] text-white"
              : "bg-white border border-[#E5E7EB] text-[#1A201A]"
          }`}
        >
          {msg.text}
          {msg.pending && <Loader2 className="inline-block w-3.5 h-3.5 ml-1 animate-spin" />}
        </div>
        {msg.actions?.length > 0 && msg.actions.map((a, i) => (
          <SuggestionCard
            key={i}
            action={a}
            busy={busyActionId === (a.appointment_id + i)}
            onAccept={(act, notify) => onAccept(act, notify, a.appointment_id + i)}
            onReject={onReject}
          />
        ))}
      </div>
    </div>
  );
}

export default function ChatAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Hallo! Ich bin dein Planungs-Assistent. Ich kenne alle Termine der nächsten 14 Tage und die Wetterprognose für Heitersheim. Frag mich, welche Termine wettergefährdet sind oder lass mich einen besseren Slot vorschlagen.",
      actions: [],
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  async function send(customText) {
    const text = (customText ?? input).trim();
    if (!text || busy) return;
    setInput("");
    const nextMsgs = [...messages, { role: "user", text }, { role: "assistant", text: "", pending: true, actions: [] }];
    setMessages(nextMsgs);
    setBusy(true);
    try {
      const res = await api.post("/assistant/chat", { session_id: SESSION_ID, message: text });
      setMessages((cur) => {
        const copy = [...cur];
        copy[copy.length - 1] = {
          role: "assistant",
          text: res.data.text || "…",
          actions: res.data.actions || [],
        };
        return copy;
      });
    } catch (e) {
      setMessages((cur) => {
        const copy = [...cur];
        copy[copy.length - 1] = { role: "assistant", text: "Fehler: " + formatApiError(e), actions: [] };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  }

  async function acceptSuggestion(action, notify, actionKey) {
    setActionBusy(actionKey);
    try {
      await api.post(`/appointments/${action.appointment_id}/reschedule`, {
        new_starts_at: action.suggested_iso,
        reason: action.reason,
        notify_customer: notify,
      });
      toast.success(
        notify
          ? "Termin verschoben und Kunde benachrichtigt"
          : "Termin verschoben"
      );
      // remove the accepted suggestion from messages
      setMessages((cur) => cur.map((m) => ({
        ...m,
        actions: (m.actions || []).filter((a) => a.appointment_id !== action.appointment_id),
      })));
      // Fire a global event so Appointments page can refresh
      window.dispatchEvent(new CustomEvent("blessing:appointments-updated"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActionBusy(null);
    }
  }

  function rejectSuggestion(action) {
    setMessages((cur) => cur.map((m) => ({
      ...m,
      actions: (m.actions || []).filter((a) => a.appointment_id !== action.appointment_id),
    })));
    toast.info("Vorschlag verworfen");
  }

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="assistant-fab"
        aria-label={open ? "Assistent schließen" : "Assistent öffnen"}
        className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] right-4 md:bottom-6 md:right-6 z-40 w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#2C3E2D] hover:bg-[#1A201A] text-white shadow-lg flex items-center justify-center transition-all hover:scale-105 group"
      >
        {open ? <X className="w-5 h-5" /> : (
          <>
            <Bot className="w-6 h-6" />
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#6B7F3E] border-2 border-white animate-pulse" />
          </>
        )}
      </button>

      {open && (
        <div
          data-testid="assistant-panel"
          className="fixed bottom-24 right-6 z-40 w-[92vw] sm:w-[420px] h-[75vh] max-h-[720px] max-md:left-3 max-md:right-3 max-md:w-auto max-md:h-auto max-md:max-h-none max-md:top-[calc(4rem+env(safe-area-inset-top))] max-md:bottom-[calc(8.5rem+env(safe-area-inset-bottom))] bg-[#FAFAF5] rounded-2xl border border-[#E5E7EB] shadow-2xl flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200"
        >
          <div className="bg-[#2C3E2D] text-white px-5 py-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#6B7F3E] flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-sm font-semibold">Planer-Assistent</div>
              <div className="text-[10px] text-[#B8C1A9]">Wetter-basierte Terminplanung · Live</div>
            </div>
            <div className="flex items-center gap-1.5 text-[10px] bg-[#3F5540] rounded-full px-2 py-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#95A97A] animate-pulse" /> aktiv
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" ref={scrollRef}>
            {messages.map((m, i) => (
              <Message
                key={i} msg={m}
                onAccept={acceptSuggestion} onReject={rejectSuggestion}
                busyActionId={actionBusy}
              />
            ))}
          </div>

          {messages.length <= 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5">
              {QUICK_PROMPTS.map((p, i) => (
                <button
                  key={i}
                  onClick={() => send(p)}
                  className="text-[11px] bg-white border border-[#E5E7EB] rounded-full px-2.5 py-1 text-[#2C3E2D] hover:border-[#6B7F3E] hover:bg-[#F5F6F0]"
                  data-testid={`assistant-quick-${i}`}
                >
                  {p}
                </button>
              ))}
            </div>
          )}

          <div className="border-t border-[#E5E7EB] p-3 bg-white">
            <div className="flex items-end gap-2">
              <Textarea
                data-testid="assistant-input"
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="Frage stellen oder Anweisung geben…"
                className="resize-none flex-1 min-h-[40px] max-h-32"
              />
              <Button
                size="icon"
                className="bg-[#6B7F3E] hover:bg-[#5A6B34] text-white h-10 w-10"
                onClick={() => send()}
                disabled={busy || !input.trim()}
                data-testid="assistant-send-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
            <div className="mt-1.5 text-[10px] text-[#5C6B5D] flex items-center gap-1">
              <CalendarClock className="w-3 h-3" /> Enter zum Senden · Shift+Enter für neue Zeile
            </div>
          </div>
        </div>
      )}
    </>
  );
}
